<template>
	<div class="spec-item">
		<div v-if="showCategory" class="spec-item__category">
			{{ category }}
		</div>
		<div class="spec-item__info">
			<p class="spec-item__text">
				<template v-if="hasSpecIcon">
					<span
						class="spec-item__icon"
						:class="`spec-item__icon--${specIcon.class}`"
						v-html="specIcon.icon"
					/>
				</template>
				{{ itemName }}
				<template v-if="isPartCase">
					<promo-sales-timed class="holiday-tag">
						<template>
							<holiday-tag :build="item" />
						</template>
					</promo-sales-timed>
				</template>
				<template v-if="isPartRam">
					<small>(max speed)</small>
					<template v-if="canDisplayRamInfo">
						<span
							v-html="computeRamCapacity(item.capacity, item.slots, item.quantity)"
						/>
						<a
							href="https://support.nzxt.com/hc/en-us/articles/360038630493-RAM-XMP-Guide-FAQ"
							target="_blank"
						>
							More Info
						</a>
					</template>
				</template>
			</p>
			<help-displayer-icon v-if="hasPartInformation" icon-color="#657583">
				<p class="spec-item__help-text">
					{{ item.part_information }}
				</p>
			</help-displayer-icon>
		</div>
	</div>
</template>

<script>
import { computeRamCapacity } from "@u/store-helpers";
import _isEmpty from "lodash/isEmpty";
import HelpDisplayerIcon from "@c/generals/help-displayer-icon";
import ItemStrategy from "./item-strategy";
import PromoSalesTimed from "@/components/promo-sales-timed";
import HolidayTag from "@c/generals/holiday-tag";

export default {
	name: "SpecItem",
	components: {
		HelpDisplayerIcon,
		PromoSalesTimed,
		HolidayTag
	},
	props: {
		specIcon: {
			type: Object,
			default() {
				return {};
			}
		},
		category: {
			type: String,
			required: true
		},
		showCategory: {
			type: Boolean,
			required: true
		},
		showMoreSpecs: {
			type: Boolean,
			required: true
		},
		partName: {
			type: String,
			default: ""
		},
		parts: {
			type: Object,
			default() {
				return {};
			}
		}
	},
	computed: {
		isTableExpanded() {
			return this.showMoreSpecs;
		},
		item() {
			const strategy = ItemStrategy.has(this.partName)
				? this.partName
				: "default";
			return ItemStrategy.strategies[strategy].get({
				parts: this.parts,
				name: this.partName
			});
		},
		hasPartInformation() {
			return this.item.part_information !== "";
		},
		hasSpecIcon() {
			const hasIcon = !_isEmpty(this.specIcon);
			return hasIcon && this.hasPartInformation;
		},
		itemName() {
			return this.isTableExpanded ? this.item.name : this.item.short_name;
		},
		isPartCase() {
			return this.partName == "cases";
		},
		isPartRam() {
			return this.partName == "ram";
		},
		canDisplayRamInfo() {
			return this.isTableExpanded && this.isPartRam;
		}
	},
	methods: {
		computeRamCapacity
	}
};
</script>

<style lang="scss" scoped>
.spec-item {
	display: flex;
	align-items: center;
	width: 100%;

	&__category {
		width: 90px;
		color: #444259;
		font-size: 14px;
		font-weight: bold;
		flex-shrink: 0;
		margin-right: 20px;
	}

	&__info {
		display: flex;
		align-items: center;
		width: 100%;
		justify-content: space-between;
	}

	&__text {
		color: #5f707f;
		margin: 0;
		max-width: 85%;
		letter-spacing: 0;
		font-size: 14px;
		line-height: 16px;
		margin-left: 15px;
		position: relative;

		a {
			color: #52007a;
			font-size: 85%;
			text-decoration: underline;
		}

		@media (min-width: 991px) and (max-width: 1200px) {
			max-width: 90%;
		}

		@media (min-width: 1200px) {
			max-width: 265px;
			font-size: 16px;
			line-height: 19px;
		}
	}

	&__icon--cross,
	&__icon--diamond {
		position: absolute;
		left: -15px;
	}

	&__icon--cross {
		color: #e45e39;
	}

	&__icon--diamond {
		color: #0076ff;
	}

	&__help-text {
		color: #ffffff;
		font-size: 16px;
		letter-spacing: 0;
		line-height: 19px;
		padding: 15px;
		margin: 0;
	}
}

.spec-item::v-deep {
	.help-tooltip {
		transform: translate(-95%, -120%);
	}

	.holiday-tag {
		display: inline-block;
		margin-left: 5px;
	}
}
</style>
